import type { Message } from '../../types/state'
import { UserAction } from '../constants/enums'
import { MfcCrc32 } from '../libraries/mfc/MfcCrc32'
import { ManageListsType, manageListsApi } from '../mfcLogic/api/lists'
import { getLens } from '../mfcLogic/lens'
import { getRoomsState, resolveID } from '../state/getState'
import { setRoomMessagesState } from '../state/state.rooms'
import { setGuestList } from '../utilities/guestList'
import { loadUserIfNeeded } from './loadUserIfNeeded'
import { hidePM } from './message'
import { userAction } from './workerActions'

export const loadUserExtras = (identifier: string | number) => {
    userAction(UserAction.LOAD_EXTRAS, { identifier })
}

export const refreshUserState = (id: number) => {
    if (!id) return
    const reqId = Math.abs(MfcCrc32.string(String(id) + Date.now()))
    userAction(UserAction.SOCKET_LOOKUP_USER, {
        identifier: id,
        reqId,
        force: true,
    })
}

export const removeBookmark = (
    identifier: string | number,
    isAuthenticated?: boolean,
) => {
    const id = resolveID(identifier)
    if (!id) return
    if (!isAuthenticated) {
        setGuestList(id, 'bookmark', true)
    }
    getLens()?.Bookmark_Remove()
    userAction(UserAction.REMOVE_BOOKMARK, { id })
}

export const removeFriend = (
    identifier: string | number,
    isAuthenticated?: boolean,
) => {
    const id = resolveID(identifier)
    if (!id) return
    if (!isAuthenticated) {
        setGuestList(id, 'friend', true)
    }
    getLens()?.Friend_Remove()
    userAction(UserAction.REMOVE_FRIEND, { id })
}

export const addFriend = async (
    slugOrId: string | number,
    isAuthenticated?: boolean,
) => {
    const user = await loadUserIfNeeded(slugOrId)

    if (!user) return
    getLens()?.Friend_Add()

    const { id, isFriend, isBookmark, slug } = user
    const { [id]: room } = getRoomsState()

    if (!isFriend && !isAuthenticated) {
        setGuestList(id, 'friend')
    }

    if (room?.isOpen && !isFriend) {
        // show the in chat notification if in the room and adding a friend
        addFriendBookmarkMessageToRoom(slug, {
            isBookmark,
            isFriend: true,
        })
    }

    userAction(UserAction.ADD_FRIEND, { id })
}

export const bookmarkModel = async (
    slugOrId: string | number,
    isAuthenticated?: boolean,
) => {
    const user = await loadUserIfNeeded(slugOrId)

    if (!user) return
    getLens()?.Bookmark_Add()

    const { id, isBookmark, isFriend, slug } = user
    const { [id]: room } = getRoomsState()

    if (!isBookmark && !isAuthenticated) {
        setGuestList(id, 'bookmark')
    }

    if (room?.isOpen && !isBookmark) {
        // show the in chat notification if in the room and adding a bookmark
        addFriendBookmarkMessageToRoom(slug, {
            isBookmark: true,
            isFriend,
        })
    }

    userAction(UserAction.ADD_BOOKMARK, { id })
}

export const ignoreUser = async (slugOrId: string | number) => {
    const user = await loadUserIfNeeded(slugOrId)
    if (!user) return
    const { id, isIgnored } = user
    const action = isIgnored ? UserAction.REMOVE_IGNORE : UserAction.ADD_IGNORE
    if (isIgnored) getLens()?.Ignore_Remove()
    else getLens()?.Ignore_Add()
    userAction(action, { id })
    await manageListsApi(ManageListsType.ignores, [id], { remove: isIgnored })
    if (!user?.isModel) hidePM(id)
}

export const hideModel = async (slugOrId: string | number) => {
    const user = await loadUserIfNeeded(slugOrId)
    if (!user) return
    const { id, isHidden } = user
    const action = isHidden ? UserAction.UNHIDE_USER : UserAction.HIDE_USER
    if (isHidden) getLens()?.Hide_Remove()
    else getLens()?.Hide_Add()
    userAction(action, { id })
    await manageListsApi(ManageListsType.hidden, [id], { remove: isHidden })
}

export const recoverFromKick = (id: number) => {
    userAction(UserAction.RECOVER_FROM_KICK, { id })
}

export const modelGetSocialMedia = async (slugOrId: string | number) => {
    const user = await loadUserIfNeeded(slugOrId)
    if (!user) return
    const { id, instagram, twitter } = user
    if (instagram || twitter) return
    userAction(UserAction.LOAD_SOCIAL_MEDIA, { id })
}

async function addFriendBookmarkMessageToRoom(
    slugOrId: string | number,
    newStatus: { isBookmark: boolean; isFriend: boolean },
) {
    const model = await loadUserIfNeeded(slugOrId)
    if (!model) return
    const { isBookmark, isFriend } = newStatus
    const { name, id } = model
    // the record can still be an { id } stub while worker data streams in
    if (!name) return
    const message: Message = { text: '', system: true }
    if (isBookmark && !isFriend) {
        message.text = `${name} added as a Bookmark`
    } else if (isBookmark && isFriend) {
        message.text = `${name} is now a Bookmarked Friend`
    } else if (isFriend && !isBookmark) {
        message.text = `${name} added as a Friend`
    }

    setRoomMessagesState(message, id)
}
